/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ConversationsTable.tsx - Gestión de Conversaciones con Sistema de Memoria
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @description Tab de conversaciones con sistema de memoria IA integrado
 * @version 2.1.0 - PASO 1: Integración DB Contactos
 * @architecture WASP Frontend
 * 
 * CAMBIOS v2.1.0 - PASO 1:
 * - ✅ Integración con useContactsDatabase para nombres
 * - ✅ Función getContactDisplayName() con prioridad nickname > name > phone
 * - ✅ Normalización de números chilenos (+56, 56, sin prefijo)
 * 
 * CARACTERÍSTICAS v2.0.0:
 * - ✅ Sistema de memoria por conversación (pause/resume)
 * - ✅ Estado isPaused individual por chat
 * - ✅ Integración con endpoints /api/memory/
 * - ✅ Props pasadas a ConversationWindow y ConversationBubble
 * - ✅ Toggle desde ventana o burbuja
 * - ✅ Switch Toggle IA/Manual reemplaza botón "Ver"
 * - ✅ Campo de Etiqueta/Categoría editable
 * - ✅ Sistema de Rating con 5 estrellas clickeable
 * - ✅ Meter circular de temperatura conversacional (0-100)
 * - ✅ Estados extendidos: Human (amarillo) y Atención (rojo)
 * - ✅ Detección de palabra clave "abracadabra" para urgencia
 * - ✅ Sistema de baneos (permanent/temporary)
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Badge, Table, Toast, ToastContainer, Modal, Form, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faComments, 
  faWindowMaximize, 
  faSync, 
  faCheckCircle,
  faEnvelope,
  faTrashAlt,
  faExclamationTriangle,
  faTimes,
  faRobot,
  faUser,
  faStar as faStarSolid,
  faEllipsisV,
  faBan,
  faUnlock,
  faClock,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';
import axios from 'axios';

import { useConversations } from '../hooks/useConversations';
import { useWindowManager } from '../hooks/useWindowManager';
import { useContactNames } from '../hooks/useContactNames';
import { useContactsDatabase } from '../hooks/useContactsDatabase';
import { useUserContext } from '../layouts/UserContext';
import { getHatateToken } from '../helpers/utils';

import ConversationWindow from '../components/ConversationWindow';
import ConversationBubble from '../components/ConversationBubble';

interface ConversationsTableProps {
  language?: 'es' | 'en';
  refreshInterval?: number;
  copilotEnabled?: boolean;
}

const TemperatureMeter: React.FC<{ temperature: number }> = ({ temperature }) => {
  const getTemperatureColor = (temp: number): string => {
    if (temp <= 20) return '#0dcaf0';
    if (temp <= 40) return '#198754';
    if (temp <= 60) return '#ffc107';
    if (temp <= 80) return '#fd7e14';
    return '#dc3545';
  };

  const color = getTemperatureColor(temperature);
  const circumference = 2 * Math.PI * 16;
  const offset = circumference - (temperature / 100) * circumference;

  return (
    <div style={{ position: 'relative', width: '42px', height: '42px' }}>
      <svg width="42" height="42" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="21" cy="21" r="16" fill="none" stroke="#e9ecef" strokeWidth="3.5" />
        <circle
          cx="21"
          cy="21"
          r="16"
          fill="none"
          stroke={color}
          strokeWidth="3.5"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
      </svg>
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: '0.65rem',
        fontWeight: '700',
        color: color
      }}>
        {temperature}
      </div>
    </div>
  );
};

const StarRating: React.FC<{ rating: number; onChange: (rating: number) => void }> = ({ rating, onChange }) => {
  const [hover, setHover] = useState(0);

  return (
    <div style={{ display: 'flex', gap: '1px', justifyContent: 'center' }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <FontAwesomeIcon
          key={star}
          icon={(hover || rating) >= star ? faStarSolid : faStarRegular}
          style={{
            color: (hover || rating) >= star ? '#ffc107' : '#dee2e6',
            cursor: 'pointer',
            fontSize: '0.85rem',
            transition: 'all 0.2s'
          }}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          onClick={(e) => {
            e.stopPropagation();
            onChange(star);
          }}
        />
      ))}
    </div>
  );
};

/**
 * Sistema de Cache para Temperaturas Conversacionales
 * - Persiste temperaturas en localStorage
 * - Muestra valores instantáneamente (experiencia fluida)
 * - Actualiza silenciosamente en background
 * - TTL: 3 minutos (invalidación automática)
 */
class TemperatureCache {
  private static CACHE_KEY = 'diana_conversation_temperatures';
  private static CACHE_TTL = 180000; // 3 minutos en ms

  /**
   * Guardar temperatura en cache
   */
  static set(conversationId: string, temperature: number): void {
    try {
      const cache = this.getAll();
      cache[conversationId] = {
        temperature,
        timestamp: Date.now()
      };
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.warn('[CACHE] Error saving temperature:', error);
    }
  }

  /**
   * Obtener temperatura del cache
   * Retorna null si no existe o está expirado
   */
  static get(conversationId: string): number | null {
    try {
      const cache = this.getAll();
      const entry = cache[conversationId];
      
      if (!entry) return null;
      
      // Verificar si está expirado
      const age = Date.now() - entry.timestamp;
      if (age > this.CACHE_TTL) {
        // Expirado, eliminar
        this.remove(conversationId);
        return null;
      }
      
      return entry.temperature;
    } catch (error) {
      console.warn('[CACHE] Error reading temperature:', error);
      return null;
    }
  }

  /**
   * Obtener todo el cache
   */
  private static getAll(): Record<string, { temperature: number; timestamp: number }> {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      return cached ? JSON.parse(cached) : {};
    } catch (error) {
      console.warn('[CACHE] Error reading cache:', error);
      return {};
    }
  }

  /**
   * Eliminar entrada del cache
   */
  static remove(conversationId: string): void {
    try {
      const cache = this.getAll();
      delete cache[conversationId];
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.warn('[CACHE] Error removing from cache:', error);
    }
  }

  /**
   * Limpiar cache completo (útil para debugging)
   */
  static clear(): void {
    try {
      localStorage.removeItem(this.CACHE_KEY);
      console.log('[CACHE] Temperature cache cleared');
    } catch (error) {
      console.warn('[CACHE] Error clearing cache:', error);
    }
  }

  /**
   * Obtener estadísticas del cache
   */
  static getStats(): { total: number; expired: number; valid: number } {
    const cache = this.getAll();
    const entries = Object.entries(cache);
    const now = Date.now();
    
    let expired = 0;
    let valid = 0;
    
    entries.forEach(([_, entry]) => {
      const age = now - entry.timestamp;
      if (age > this.CACHE_TTL) {
        expired++;
      } else {
        valid++;
      }
    });
    
    return { total: entries.length, expired, valid };
  }
}

export const ConversationsTable: React.FC<ConversationsTableProps> = ({
  language = 'es',
  refreshInterval = 5000,
  copilotEnabled = true
}) => {
  const { user } = useUserContext();
  const API_TOKEN_KEY = getHatateToken();
  const API_BASE_URL = 'https://backend.anubisai.net/api/whatsapp/wasp';
  const API_KEY = 'anubis-whatsapp-api-key';

  const {
    conversations,
    loading,
    error: conversationsError,
    lastUpdate,
    refreshConversations,
    deleteConversation
  } = useConversations({
    autoRefresh: true,
    refreshInterval,
    onError: (err) => showErrorToast(err)
  });

  const {
    windows,
    openWindow,
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    updateWindowPosition,
    updateWindowSize,
    bringToFront,
    isWindowOpen
  } = useWindowManager();

  const { getContactName } = useContactNames();
  const { contacts, isLoading: contactsLoading, error: contactsError } = useContactsDatabase();

  // 🔍 DEBUG: Monitorear carga de contactos
  useEffect(() => {
    console.log('[CONTACTS DEBUG] Loading:', contactsLoading);
    console.log('[CONTACTS DEBUG] Error:', contactsError);
    console.log('[CONTACTS DEBUG] Total loaded:', contacts.length);
    console.log('[CONTACTS DEBUG] User object:', user);
    console.log('[CONTACTS DEBUG] User ID:', user?._id);
    console.log('[CONTACTS DEBUG] mlStuff:', user?.mlStuff);
    if (contacts.length > 0) {
      console.log('[CONTACTS DEBUG] Sample contact:', contacts[0]);
    }
  }, [contacts, contactsLoading, contactsError, user]);

  // 🧪 TEST: Verificar endpoint de contactos directamente
  useEffect(() => {
    if (!user?._id) return;

    const testContactsEndpoint = async () => {
      try {
        console.log('[API TEST] Testing /contacts endpoint...');
        
        const response = await axios.get(
          `${API_BASE_URL}/contacts`,
          {
            headers: {
              'Authorization': API_TOKEN_KEY,
              'X-API-Key': API_KEY,
              'X-User-ID': user._id,
              'Content-Type': 'application/json'
            },
            timeout: 10000
          }
        );

        console.log('[API TEST] ✅ Response received:');
        console.log('[API TEST] - Success:', response.data.success);
        console.log('[API TEST] - Contacts count:', response.data.contacts?.length || 0);
        console.log('[API TEST] - Total:', response.data.total);
        console.log('[API TEST] - Full response:', response.data);
        
        if (response.data.contacts && response.data.contacts.length > 0) {
          console.log('[API TEST] - Sample contact:', response.data.contacts[0]);
        } else {
          console.warn('[API TEST] ⚠️ API returned empty contacts array');
        }

      } catch (error: any) {
        console.error('[API TEST] ❌ Error:', error);
        console.error('[API TEST] - Message:', error.message);
        console.error('[API TEST] - Response:', error.response?.data);
      }
    };

    // Ejecutar test solo una vez cuando el user esté disponible
    const timer = setTimeout(testContactsEndpoint, 2000);
    return () => clearTimeout(timer);
  }, [user?._id]);

  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error' | 'warning'>('success');
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [pausedConversations, setPausedConversations] = useState<Record<string, boolean>>({});
  const [togglingPause, setTogglingPause] = useState<Record<string, boolean>>({});
  const [togglingBan, setTogglingBan] = useState<Record<string, boolean>>({});

  const [conversationTags, setConversationTags] = useState<Record<string, string>>({});
  const [conversationTagColors, setConversationTagColors] = useState<Record<string, string>>({});
  const [conversationRatings, setConversationRatings] = useState<Record<string, number>>({});
  const [showColorPicker, setShowColorPicker] = useState<string | null>(null);
  const [editingColors, setEditingColors] = useState<Record<string, boolean>>({});  // ✅ NUEVO: Lock para ediciones

  // Cerrar color picker al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showColorPicker) {  // ✅ Ahora sí existe la variable
        setShowColorPicker(null);
      }
    };

    if (showColorPicker) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showColorPicker]);

  // Paleta de colores estilo Firefox
  const TAG_COLORS = [
    { name: 'Azul', value: '#0a84ff', text: '#fff' },
    { name: 'Verde', value: '#30d158', text: '#fff' },
    { name: 'Naranja', value: '#ff9f0a', text: '#000' },
    { name: 'Rojo', value: '#ff453a', text: '#fff' },
    { name: 'Morado', value: '#bf5af2', text: '#fff' },
    { name: 'Rosa', value: '#ff375f', text: '#fff' },
    { name: 'Amarillo', value: '#ffd60a', text: '#000' },
    { name: 'Turquesa', value: '#64d2ff', text: '#000' }
  ];
  const [conversationTemperatures, setConversationTemperatures] = useState<Record<string, number>>({});
  // ✅ Inicializar temperaturas desde cache (INSTANTÁNEO)
  useEffect(() => {
    if (conversations.length > 0) {
      const cachedTemperatures: Record<string, number> = {};
      
      conversations.forEach(conv => {
        const cached = TemperatureCache.get(conv.id);
        if (cached !== null) {
          cachedTemperatures[conv.id] = cached;
        } else {
          // Si no hay cache, mostrar neutral (50) como placeholder
          cachedTemperatures[conv.id] = 50;
        }
      });
      
      setConversationTemperatures(cachedTemperatures);
      console.log('[CACHE] Loaded temperatures from cache:', Object.keys(cachedTemperatures).length);
    }
  }, [conversations]);
  const [urgentFlags, setUrgentFlags] = useState<Record<string, boolean>>({});

  const [bannedConversations, setBannedConversations] = useState<Record<string, { isBanned: boolean; type?: string; expiresAt?: string }>>({});
  const [showBanModal, setShowBanModal] = useState(false);
  const [banModalData, setBanModalData] = useState<{ id: string; name: string; type: 'permanent' | 'temporary' } | null>(null);
  const [banReason, setBanReason] = useState('');
  const [banDate, setBanDate] = useState('');
  const [banTime, setBanTime] = useState('');
  const [banning, setBanning] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [editingTagId, setEditingTagId] = useState<string | null>(null);

  const [memoryLoaded, setMemoryLoaded] = useState(false);
  const [shouldRefetchMemory, setShouldRefetchMemory] = useState(false);


  const handleTogglePause = async (conversationId: string) => {
    const phoneNumber = conversationId.split('@')[0];
    const currentIsPaused = pausedConversations[conversationId] || false;
    
    setTogglingPause(prev => ({ ...prev, [conversationId]: true }));

    try {
      const endpoint = currentIsPaused
        ? `${API_BASE_URL}/memory/${phoneNumber}/resume`
        : `${API_BASE_URL}/memory/${phoneNumber}/pause`;

      const response = await axios.post(
        endpoint,
        {},
        {
          headers: {
            'Authorization': API_TOKEN_KEY,
            'x-api-key': API_KEY,
            'Content-Type': 'application/json'
          },
          timeout: 10000
        }
      );

      if (response.data.success) {
        setPausedConversations(prev => ({
          ...prev,
          [conversationId]: response.data.isPaused
        }));

        showSuccessToast(response.data.message || `Modo ${response.data.isPaused ? 'Manual' : 'IA'} activado`);
      } else {
        throw new Error(response.data.message || 'Toggle failed');
      }
    } catch (error: any) {
      console.error('[MEMORY] Error toggling pause:', error);
      showErrorToast(error.response?.data?.message || 'Error al cambiar modo');
    } finally {
      setTogglingPause(prev => ({ ...prev, [conversationId]: false }));
    }
  };

  useEffect(() => {
    const fetchMemoryStates = async () => {
      if (!conversations || conversations.length === 0) return;

      // ✅ Solo cargar si:
      // 1. Es la primera vez (memoryLoaded = false)
      // 2. Se solicitó explícitamente (shouldRefetchMemory = true)
      if (!memoryLoaded && !shouldRefetchMemory) {
        console.log('[MEMORY] 📥 Cargando estados de memoria (primera vez)...');
      } else if (shouldRefetchMemory) {
        console.log('[MEMORY] 🔄 Recargando estados de memoria (on-demand)...');
        setShouldRefetchMemory(false);
      } else {
        console.log('[MEMORY] ⏭️ Skip: Memoria ya cargada, esperando cambio explícito');
        return; // ✅ NO recargar en auto-refresh
      }

      try {
        const statePromises = conversations.map(async (conv) => {
          const phoneNumber = conv.id.split('@')[0];
          
          try {
            const response = await axios.get(
              `${API_BASE_URL}/memory/${phoneNumber}/status`,
              {
                headers: {
                  'Authorization': API_TOKEN_KEY,
                  'x-api-key': API_KEY
                },
                timeout: 5000
              }
            );

            if (response.data.success) {
              return {
                conversationId: conv.id,
                isPaused: response.data.isPaused || false,
                rating: response.data.memory?.conversationMetrics?.rating || 0,
                tag: response.data.memory?.conversationMetrics?.tag || '',
                tagColor: response.data.memory?.conversationMetrics?.tagColor || ''
              };
            }
          } catch (err) {
            console.warn(`[MEMORY] Could not fetch status for ${phoneNumber}:`, err);
          }

          return { 
            conversationId: conv.id, 
            isPaused: false,
            rating: 0,
            tag: '',
            tagColor: ''
          };
        });

        const states = await Promise.all(statePromises);
        
        const statesMap: Record<string, boolean> = {};
        const ratingsMap: Record<string, number> = {};
        const tagsMap: Record<string, string> = {};
        const tagColorsMap: Record<string, string> = {};
        
        states.forEach(state => {
          statesMap[state.conversationId] = state.isPaused;
          ratingsMap[state.conversationId] = state.rating;
          
          // Solo actualizar tag si NO se está editando
          if (editingTagId !== state.conversationId) {
            tagsMap[state.conversationId] = state.tag;
          } else {
            tagsMap[state.conversationId] = conversationTags[state.conversationId] || '';
          }
          
          // Solo actualizar color si NO está siendo editado
          if (!editingColors[state.conversationId]) {
            tagColorsMap[state.conversationId] = state.tagColor;
          } else {
            tagColorsMap[state.conversationId] = conversationTagColors[state.conversationId] || '';
          }
        });

        setPausedConversations(statesMap);
        setConversationRatings(ratingsMap);
        setConversationTags(prev => ({ ...prev, ...tagsMap }));
        setConversationTagColors(prev => {
          const merged = { ...prev };
          Object.entries(tagColorsMap).forEach(([id, color]) => {
            if (!editingColors[id]) {
              merged[id] = color;
            }
          });
          return merged;
        });
        
        setMemoryLoaded(true);  // ✅ Marcar como cargado
        
        console.log('[MEMORY] ✅ Estados cargados:', {
          pause: Object.keys(statesMap).length,
          ratings: Object.keys(ratingsMap).length,
          tags: Object.keys(tagsMap).length,
          colors: Object.keys(tagColorsMap).length
        });
        
      } catch (error) {
        console.error('[MEMORY] Error fetching memory states:', error);
      }
    };

    fetchMemoryStates();
  }, [conversations.length, shouldRefetchMemory]); 

  useEffect(() => {
    const fetchBanStates = async () => {
      if (!conversations || conversations.length === 0) return;

      try {
        const banPromises = conversations.map(async (conv) => {
          const jid = conv.id;
          
          try {
            const response = await axios.get(
              `${API_BASE_URL}/banned/check/${jid}`,
              {
                headers: {
                  'Authorization': API_TOKEN_KEY,
                  'x-api-key': API_KEY
                },
                timeout: 5000
              }
            );

            if (response.data.success) {
              return {
                conversationId: conv.id,
                isBanned: response.data.isBanned || false,
                type: response.data.info?.type,
                expiresAt: response.data.info?.expiresAt
              };
            }
          } catch (err) {
            console.warn(`[BAN] Could not fetch ban status for ${jid}:`, err);
          }

          return { conversationId: conv.id, isBanned: false };
        });

        const states = await Promise.all(banPromises);
        
        const banMap: Record<string, { isBanned: boolean; type?: string; expiresAt?: string }> = {};
        states.forEach(state => {
          banMap[state.conversationId] = {
            isBanned: state.isBanned,
            type: state.type,
            expiresAt: state.expiresAt
          };
        });

        setBannedConversations(banMap);
        
      } catch (error) {
        console.error('[BAN] Error fetching ban states:', error);
      }
    };

    fetchBanStates();
  }, [conversations]);

  const fetchConversationTemperature = async (conversationId: string): Promise<number> => {
    try {
      console.log('[TEMPERATURE] Fetching for:', conversationId);
      
      const response = await axios.get(
        `https://backend.anubisai.net/api/diana/temperature/${conversationId}`,
        {
          headers: {
            'Authorization': API_TOKEN_KEY,
            'x-api-key': API_KEY  // ✅ Minúscula (consistente con otros endpoints)
          },
          timeout: 30000  // ✅ 30 segundos (igual que proxy)
        }
      );
      
      console.log('[TEMPERATURE] Response:', response.data);
      
      // Verificar que la respuesta es válida
      if (response.data.success && typeof response.data.temperature === 'number') {
        return response.data.temperature;
      }
      
      // Si no hay temperatura válida, retornar neutral (50)
      console.warn('[TEMPERATURE] Invalid response, using default 50');
      return 50;
      
    } catch (error: any) {
      console.error('[TEMPERATURE] Error:', error.message);
      if (error.response?.data) {
        console.error('[TEMPERATURE] Server response:', error.response.data);
      }
      // En caso de error, retornar neutral
      return 50;
    }
  };

  const handleBanPermanent = async (conversationId: string, displayName: string) => {
    setBanModalData({ id: conversationId, name: displayName, type: 'permanent' });
    setShowBanModal(true);
  };

  const handleBanTemporary = async (conversationId: string, displayName: string) => {
    setBanModalData({ id: conversationId, name: displayName, type: 'temporary' });
    setShowBanModal(true);
  };

  const handleUnban = async (conversationId: string) => {
    setTogglingBan(prev => ({ ...prev, [conversationId]: true })); 
    try {
      const jid = conversationId;

      const response = await axios.delete(
        `${API_BASE_URL}/banned/${jid}`,
        {
          headers: {
            'Authorization': API_TOKEN_KEY,
            'x-api-key': API_KEY,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setBannedConversations(prev => ({
          ...prev,
          [conversationId]: { isBanned: false }
        }));
        showSuccessToast('Usuario desbloqueado correctamente');
      }
    } catch (error: any) {
      console.error('[BAN] Error unbanning:', error);
      showErrorToast(error.response?.data?.message || 'Error al desbloquear usuario');
    } finally {
      setTogglingBan(prev => ({ ...prev, [conversationId]: false }));
    }
  };

  const confirmBan = async () => {
    if (!banModalData) return;

    setBanning(true);

    try {
      const jid = banModalData.id;

      if (banModalData.type === 'permanent') {
        const response = await axios.post(
          `${API_BASE_URL}/banned/permanent`,
          { 
            jid, 
            reason: banReason || 'Bloqueado desde panel de conversaciones',
            bannedBy: user?.email || 'admin'
          },
          {
            headers: {
              'Authorization': API_TOKEN_KEY,
              'x-api-key': API_KEY,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          setBannedConversations(prev => ({
            ...prev,
            [banModalData.id]: { isBanned: true, type: 'permanent' }
          }));
          showSuccessToast('Usuario bloqueado permanentemente');
        }
      } else {
        if (!banDate || !banTime) {
          showErrorToast('Debe seleccionar fecha y hora');
          setBanning(false);
          return;
        }

        const expiresAtDate = new Date(`${banDate}T${banTime}:00`);
        const now = new Date();
        const diffMs = expiresAtDate.getTime() - now.getTime();
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        let duration: string;
        if (diffDays > 0) {
          duration = `${diffDays}d`;
        } else if (diffHours > 0) {
          duration = `${diffHours}h`;
        } else {
          duration = '1h';
        }

        const response = await axios.post(
          `${API_BASE_URL}/banned/temporary`,
          { 
            jid, 
            duration,
            reason: banReason || 'Bloqueo temporal desde panel de conversaciones',
            bannedBy: user?.email || 'admin'
          },
          {
            headers: {
              'Authorization': API_TOKEN_KEY,
              'x-api-key': API_KEY,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          setBannedConversations(prev => ({
            ...prev,
            [banModalData.id]: { 
              isBanned: true, 
              type: 'temporary',
              expiresAt: response.data.expiresAt
            }
          }));
          showSuccessToast('Usuario bloqueado temporalmente');
        }
      }

      setShowBanModal(false);
      setBanModalData(null);
      setBanReason('');
      setBanDate('');
      setBanTime('');

    } catch (error: any) {
      console.error('[BAN] Error banning:', error);
      showErrorToast(error.response?.data?.message || 'Error al bloquear usuario');
    } finally {
      setBanning(false);
    }
  };

  const cancelBan = () => {
    setShowBanModal(false);
    setBanModalData(null);
    setBanReason('');
    setBanDate('');
    setBanTime('');
  };

  const handleTagChange = (conversationId: string, tag: string) => {
    setConversationTags(prev => ({ ...prev, [conversationId]: tag }));
  };

  const handleTagSave = async (conversationId: string, tag: string, color?: string) => {
    const phoneNumber = conversationId.split('@')[0];
    
    // Limpiar estado de edición de texto
    setEditingTagId(null);
    
    // ✅ NUEVO: Si se guardó un color, activar lock temporal
    if (color !== undefined) {
      setEditingColors(prev => ({ ...prev, [conversationId]: true }));
      console.log('[TAG] 🔒 Lock activado para color de:', conversationId);
    }
    
    try {
      const updateData: any = { tag };
      if (color !== undefined) {  // ✅ Permitir guardar color vacío ('')
        updateData.tagColor = color;
      }
      
      const response = await axios.post(
        `${API_BASE_URL}/memory/${phoneNumber}/update`,
        {
          conversationMetrics: updateData
        },
        {
          headers: {
            'Authorization': API_TOKEN_KEY,
            'x-api-key': API_KEY,
            'Content-Type': 'application/json'
          },
          timeout: 10000
        }
      );

      if (response.data.success) {
        console.log('[TAG] ✅ Saved successfully:', { phoneNumber, tag, color });
        showSuccessToast(color !== undefined ? 'Color guardado' : 'Etiqueta guardada');
        
        // ✅ NUEVO: Liberar lock después de 3 segundos (tiempo para que backend actualice)
        if (color !== undefined) {
          setTimeout(() => {
            setEditingColors(prev => {
              const updated = { ...prev };
              delete updated[conversationId];
              console.log('[TAG] 🔓 Lock liberado para:', conversationId);
              return updated;
            });
          }, 3000);  // 3 segundos de protección
        }
      } else {
        throw new Error(response.data.message || 'Failed to save tag');
      }
    } catch (error: any) {
      console.error('[TAG] ❌ Error saving:', error);
      console.error('[TAG] Response:', error.response?.data);
      showErrorToast('Error al guardar etiqueta');
      
      // ✅ NUEVO: Liberar lock en caso de error
      if (color !== undefined) {
        setEditingColors(prev => {
          const updated = { ...prev };
          delete updated[conversationId];
          return updated;
        });
      }
    }
  };

  const handleRatingChange = async (conversationId: string, rating: number) => {
    const phoneNumber = conversationId.split('@')[0];
    
    // Actualizar estado local inmediatamente (optimistic update)
    setConversationRatings(prev => ({ ...prev, [conversationId]: rating }));

    try {
      const response = await axios.post(
        `${API_BASE_URL}/memory/${phoneNumber}/update`,
        {
          conversationMetrics: {
            rating: rating
          }
        },
        {
          headers: {
            'Authorization': API_TOKEN_KEY,
            'x-api-key': API_KEY,
            'Content-Type': 'application/json'
          },
          timeout: 10000
        }
      );

      if (response.data.success) {
        console.log('[RATING] Saved successfully:', { phoneNumber, rating });
        showSuccessToast(`Rating: ${rating} estrellas`);
      } else {
        throw new Error(response.data.message || 'Failed to save rating');
      }
    } catch (error: any) {
      console.error('[RATING] Error saving:', error);
      console.error('[RATING] Response:', error.response?.data);
      // Revertir cambio optimista
      setConversationRatings(prev => ({ ...prev, [conversationId]: 0 }));
      showErrorToast('Error al guardar rating');
    }
  };

  useEffect(() => {
    if (conversations.length > 0) {
      // Cargar temperaturas para conversaciones nuevas de forma secuencial
      const loadTemperaturesSequentially = async () => {
        for (let i = 0; i < conversations.length; i++) {
          const conv = conversations[i];
          if (!conversationTemperatures[conv.id]) {
            const temp = await fetchConversationTemperature(conv.id);
            setConversationTemperatures(prev => ({ ...prev, [conv.id]: temp }));
            
            // Delay de 500ms entre requests para no saturar
            if (i < conversations.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
        }
      };
      
      loadTemperaturesSequentially();
    }
  }, [conversations]);

  // Refresh periódico de temperaturas cada 3 minutos
  useEffect(() => {
    if (conversations.length > 0) {
      const loadTemperaturesInBackground = async () => {
        console.log('[TEMPERATURE] Starting background update...');
        
        const BATCH_SIZE = 3; // 3 requests simultáneos
        
        for (let i = 0; i < conversations.length; i += BATCH_SIZE) {
          const batch = conversations.slice(i, i + BATCH_SIZE);
          
          // Procesar batch en paralelo
          await Promise.all(
            batch.map(async (conv) => {
              try {
                const temp = await fetchConversationTemperature(conv.id);
                
                // Actualizar estado
                setConversationTemperatures(prev => ({ ...prev, [conv.id]: temp }));
                
                // Guardar en cache
                TemperatureCache.set(conv.id, temp);
                
              } catch (error) {
                console.error(`[TEMPERATURE] Failed for ${conv.id}:`, error);
                // En caso de error, mantener valor del cache o neutral
                const cached = TemperatureCache.get(conv.id);
                if (cached !== null) {
                  setConversationTemperatures(prev => ({ ...prev, [conv.id]: cached }));
                } else {
                  setConversationTemperatures(prev => ({ ...prev, [conv.id]: 50 }));
                }
              }
            })
          );
          
          // Pequeño delay entre batches
          if (i + BATCH_SIZE < conversations.length) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        }
        
        console.log('[TEMPERATURE] Background update complete');
      };
      
      // ✅ CRÍTICO: Ejecutar sin await para no bloquear
      loadTemperaturesInBackground().catch(err => {
        console.error('[TEMPERATURE] Background loading error:', err);
      });
    }
  }, [conversations]);

  useEffect(() => {
    const refreshTemperatures = async () => {
      if (conversations.length === 0) return;
      
      console.log('[TEMPERATURE] Starting periodic refresh...');
      
      const BATCH_SIZE = 3;
      
      for (let i = 0; i < conversations.length; i += BATCH_SIZE) {
        const batch = conversations.slice(i, i + BATCH_SIZE);
        
        await Promise.all(
          batch.map(async (conv) => {
            try {
              const temp = await fetchConversationTemperature(conv.id);
              
              // Actualizar estado
              setConversationTemperatures(prev => ({ ...prev, [conv.id]: temp }));
              
              // Actualizar cache
              TemperatureCache.set(conv.id, temp);
              
            } catch (error) {
              console.error(`[TEMPERATURE] Refresh failed for ${conv.id}`);
              // Mantener valor anterior en caso de error
            }
          })
        );
        
        if (i + BATCH_SIZE < conversations.length) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }
      
      console.log('[TEMPERATURE] Periodic refresh complete');
    };

    // Refresh cada 3 minutos
    const interval = setInterval(refreshTemperatures, 180000);
    
    return () => clearInterval(interval);
  }, [conversations]);

  useEffect(() => {
    const flags: Record<string, boolean> = {};
    conversations.forEach(conv => {
      const hasUrgentKeyword = conv.preview?.toLowerCase().includes('abracadabra') || false;
      flags[conv.id] = hasUrgentKeyword;
    });
    setUrgentFlags(flags);
  }, [conversations]);

  const showErrorToast = (message: string) => {
    setToastMessage(message);
    setToastType('error');
    setTimeout(() => setToastMessage(null), 5000);
  };

  const showSuccessToast = (message: string) => {
    setToastMessage(message);
    setToastType('success');
    setTimeout(() => setToastMessage(null), 3000);
  };

  const formatRelativeTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return language === 'es' ? 'Ahora' : 'Now';
    if (diffMins < 60) return `${diffMins}min`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  const truncateText = (text: string, maxLength: number): string => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleConversationClick = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    
    if (!isWindowOpen(conversationId)) {
      openWindow(conversationId);
    } else {
      const window = windows.find(w => w.conversationId === conversationId);
      if (window) {
        if (window.isMinimized) {
          maximizeWindow(window.id);
        }
        bringToFront(window.id);
      }
    }
  };

  const handleDeleteConversation = (conversationId: string, contactName: string) => {
    setDeleteTarget({ id: conversationId, name: contactName });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    const success = await deleteConversation(deleteTarget.id);
    setDeleting(false);

    if (success) {
      const windowToClose = windows.find(w => w.conversationId === deleteTarget.id);
      if (windowToClose) {
        closeWindow(windowToClose.id);
      }

      if (selectedConversationId === deleteTarget.id) {
        setSelectedConversationId(null);
      }

      setShowDeleteModal(false);
      setDeleteTarget(null);

      showSuccessToast(
        language === 'es' 
          ? 'Conversación eliminada correctamente' 
          : 'Conversation deleted successfully'
      );
    } else {
      showErrorToast(
        language === 'es' 
          ? 'No se pudo eliminar la conversación' 
          : 'Could not delete the conversation'
      );
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const getConversationDisplayName = (conversationId: string, fallback: string): string => {
    return getContactName(conversationId, fallback);
  };

  /**
   * Obtiene el nombre a mostrar desde MÚLTIPLES FUENTES
   * Prioridad: 
   * 1) Nickname de DB contactos (custom_fields.nickname)
   * 2) Nombre de DB contactos (contact.name)
   * 3) Nombre de conversación WhatsApp (conversation.name)
   * 4) Número de teléfono (fallback)
   */
  const getContactDisplayName = useCallback((conversationId: string): string => {
    const phoneNumber = conversationId.split('@')[0];
    
    console.log('[DISPLAY NAME] Looking for:', phoneNumber);
    
    // PASO 1: Buscar en DB de contactos
    if (contacts.length > 0) {
      const contact = contacts.find(c => {
        const cleanPhone = c.phone.replace(/[\+\s-]/g, '');
        const cleanSearch = phoneNumber.replace(/[\+\s-]/g, '');
        
        return cleanPhone === cleanSearch || 
               cleanPhone === `56${cleanSearch}` ||
               `56${cleanPhone}` === cleanSearch;
      });
      
      if (contact) {
        // Prioridad: nickname > name
        const displayName = contact.custom_fields?.nickname || contact.name;
        console.log('[DISPLAY NAME] ✅ Found in DB:', displayName);
        return displayName;
      }
    }
    
    // PASO 2: Buscar en conversación actual (nombre de WhatsApp)
    const conversation = conversations.find(c => c.id === conversationId);
    if (conversation?.name && conversation.name !== phoneNumber) {
      console.log('[DISPLAY NAME] ✅ Found in conversation:', conversation.name);
      return conversation.name;
    }
    
    // PASO 3: Fallback a useContactNames (sistema legacy)
    const legacyName = getContactName(conversationId, '');
    if (legacyName && legacyName !== phoneNumber && legacyName !== conversationId) {
      console.log('[DISPLAY NAME] ✅ Found in legacy system:', legacyName);
      return legacyName;
    }
    
    // PASO 4: Fallback final - número de teléfono
    console.log('[DISPLAY NAME] ❌ No name found, showing phone:', phoneNumber);
    return phoneNumber;
  }, [contacts, conversations, getContactName]);

  const getConversationById = (conversationId: string) => {
    return conversations.find(conv => conv.id === conversationId);
  };

  const getStatusBadge = (conversation: any) => {
    const isBanned = bannedConversations[conversation.id]?.isBanned || false;
    const isPaused = pausedConversations[conversation.id] || false;
    const isUrgent = urgentFlags[conversation.id] || false;

    if (isBanned) {
      return { bg: 'danger', text: language === 'es' ? 'Bloqueado' : 'Banned' };
    }
    if (isUrgent) {
      return { bg: 'danger', text: language === 'es' ? 'Atención' : 'Attention' };
    }
    if (isPaused) {
      return { bg: 'warning', text: language === 'es' ? 'Human' : 'Human' };
    }
    if (conversation.isActive) {
      return { bg: 'success', text: language === 'es' ? 'Activa' : 'Active' };
    }
    return { bg: 'secondary', text: language === 'es' ? 'Inactiva' : 'Inactive' };
  };

  const activeConversations = conversations.filter(c => c.isActive).length;
  const totalMessages = conversations.reduce((sum, c) => sum + (c.messageCount || 0), 0);

  const getMinDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return { date: `${year}-${month}-${day}`, time: `${hours}:${minutes}` };
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--bs-body-bg)',
      padding: '24px',
      overflow: 'hidden'
    }}>
      <Container fluid style={{ maxWidth: '100%', padding: '0 12px' }}>
        
        <ToastContainer position="top-end" className="p-3" style={{ zIndex: 99999, position: 'fixed' }}>
          <Toast 
            show={!!toastMessage} 
            onClose={() => setToastMessage(null)}
            delay={toastType === 'error' ? 5000 : 3000}
            autohide
            style={{
              backgroundColor: toastType === 'error' ? '#dc3545' : toastType === 'warning' ? '#ffc107' : '#28a745',
              color: '#fff',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              minWidth: '300px'
            }}
          >
            <Toast.Header 
              closeButton
              style={{
                backgroundColor: 'transparent',
                color: '#fff',
                borderBottom: '1px solid rgba(255,255,255,0.2)'
              }}
            >
              <FontAwesomeIcon 
                icon={toastType === 'error' ? faExclamationTriangle : faCheckCircle} 
                className="me-2" 
              />
              <strong className="me-auto">
                {toastType === 'error' 
                  ? (language === 'es' ? 'Error' : 'Error')
                  : (language === 'es' ? 'Éxito' : 'Success')
                }
              </strong>
            </Toast.Header>
            <Toast.Body style={{ color: '#fff', padding: '12px 16px' }}>
              {toastMessage}
            </Toast.Body>
          </Toast>
        </ToastContainer>

        <Modal 
          show={showDeleteModal} 
          onHide={cancelDelete}
          centered
          backdrop="static"
          style={{ zIndex: 9999 }}
        >
          <div style={{
            borderRadius: '16px',
            overflow: 'hidden',
            backgroundColor: 'var(--bs-body-bg)'
          }}>
            <Modal.Header style={{
              backgroundColor: '#dc3545',
              color: '#fff',
              border: 'none',
              padding: '20px 24px'
            }}>
              <Modal.Title style={{ 
                fontSize: '1.25rem', 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                {language === 'es' ? '¿Eliminar conversación?' : 'Delete conversation?'}
              </Modal.Title>
              <Button
                variant="link"
                onClick={cancelDelete}
                style={{ 
                  color: '#fff', 
                  padding: '4px 8px',
                  fontSize: '1.25rem',
                  opacity: 0.8
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </Modal.Header>
            <Modal.Body style={{ padding: '24px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🗑️</div>
                <p style={{ fontSize: '1rem', color: 'var(--bs-secondary)', marginBottom: '12px' }}>
                  {language === 'es' 
                    ? 'Estás a punto de eliminar la conversación con' 
                    : 'You are about to delete the conversation with'}
                </p>
                <p style={{ 
                  fontSize: '1.25rem', 
                  fontWeight: 600, 
                  color: 'var(--bs-body-color)',
                  marginBottom: '20px',
                  wordBreak: 'break-word'
                }}>
                  {deleteTarget?.name}
                </p>
                <div style={{
                  backgroundColor: 'rgba(220, 53, 69, 0.1)',
                  borderLeft: '4px solid #dc3545',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  textAlign: 'left'
                }}>
                  <p style={{ 
                    fontSize: '0.9rem', 
                    color: '#dc3545',
                    margin: 0,
                    fontWeight: 500
                  }}>
                    ⚠️ {language === 'es' ? 'Esta acción no se puede deshacer' : 'This action cannot be undone'}
                  </p>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer style={{ 
              border: 'none', 
              padding: '16px 24px',
              backgroundColor: 'var(--bs-light)',
              gap: '12px'
            }}>
              <Button 
                variant="outline-secondary" 
                onClick={cancelDelete}
                disabled={deleting}
                style={{
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: 500,
                  flex: 1
                }}
              >
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button 
                variant="danger" 
                onClick={confirmDelete}
                disabled={deleting}
                style={{
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: 500,
                  flex: 1
                }}
              >
                {deleting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {language === 'es' ? 'Eliminando...' : 'Deleting...'}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                    {language === 'es' ? 'Sí, eliminar' : 'Yes, delete'}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </div>
        </Modal>

        <Modal 
          show={showBanModal} 
          onHide={cancelBan}
          centered
          backdrop="static"
          style={{ zIndex: 999999 }}
        >
          <div style={{
            borderRadius: '16px',
            overflow: 'hidden',
            backgroundColor: 'var(--bs-body-bg)'
          }}>
            <Modal.Header style={{
              backgroundColor: '#dc3545',
              color: '#fff',
              border: 'none',
              padding: '20px 24px'
            }}>
              <Modal.Title style={{ 
                fontSize: '1.25rem', 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <FontAwesomeIcon icon={faBan} />
                {language === 'es' ? 'Bloquear usuario' : 'Ban user'}
              </Modal.Title>
              <Button
                variant="link"
                onClick={cancelBan}
                style={{ 
                  color: '#fff', 
                  padding: '4px 8px',
                  fontSize: '1.25rem',
                  opacity: 0.8
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </Modal.Header>
            <Modal.Body style={{ padding: '24px' }}>
              <div>
                <div style={{ fontSize: '3rem', marginBottom: '20px', textAlign: 'center' }}>🚫</div>
                <p style={{ fontSize: '1rem', color: 'var(--bs-secondary)', marginBottom: '12px' }}>
                  {language === 'es' 
                    ? `Bloquear ${banModalData?.type === 'permanent' ? 'permanentemente' : 'temporalmente'} a` 
                    : `${banModalData?.type === 'permanent' ? 'Permanently' : 'Temporarily'} ban`}
                </p>
                <p style={{ 
                  fontSize: '1.25rem', 
                  fontWeight: 600, 
                  color: 'var(--bs-body-color)',
                  marginBottom: '20px',
                  wordBreak: 'break-word'
                }}>
                  {banModalData?.name}
                </p>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 500 }}>
                    {language === 'es' ? 'Razón del bloqueo' : 'Ban reason'}
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                    placeholder={language === 'es' ? 'Describe el motivo del bloqueo...' : 'Describe the reason for the ban...'}
                    style={{ borderRadius: '8px' }}
                  />
                </Form.Group>

                {banModalData?.type === 'temporary' && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ fontWeight: 500 }}>
                        {language === 'es' ? 'Fecha de desbloqueo' : 'Unban date'}
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={banDate}
                        onChange={(e) => setBanDate(e.target.value)}
                        min={getMinDateTime().date}
                        style={{ borderRadius: '8px' }}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label style={{ fontWeight: 500 }}>
                        {language === 'es' ? 'Hora de desbloqueo' : 'Unban time'}
                      </Form.Label>
                      <Form.Control
                        type="time"
                        value={banTime}
                        onChange={(e) => setBanTime(e.target.value)}
                        style={{ borderRadius: '8px' }}
                      />
                    </Form.Group>
                  </>
                )}

                <div style={{
                  backgroundColor: 'rgba(220, 53, 69, 0.1)',
                  borderLeft: '4px solid #dc3545',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginTop: '20px'
                }}>
                  <p style={{ 
                    fontSize: '0.9rem', 
                    color: '#dc3545',
                    margin: 0,
                    fontWeight: 500
                  }}>
                    ⚠️ {language === 'es' 
                      ? 'El usuario no podrá enviar mensajes ni interactuar con el bot' 
                      : 'User will not be able to send messages or interact with the bot'}
                  </p>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer style={{ 
              border: 'none', 
              padding: '16px 24px',
              backgroundColor: 'var(--bs-light)',
              gap: '12px'
            }}>
              <Button 
                variant="outline-secondary" 
                onClick={cancelBan}
                disabled={banning}
                style={{
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: 500,
                  flex: 1
                }}
              >
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button 
                variant="danger" 
                onClick={confirmBan}
                disabled={banning}
                style={{
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: 500,
                  flex: 1
                }}
              >
                {banning ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {language === 'es' ? 'Bloqueando...' : 'Banning...'}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faBan} className="me-2" />
                    {language === 'es' ? 'Bloquear' : 'Ban'}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </div>
        </Modal>

        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <h3 style={{ 
                  fontWeight: 700, 
                  color: 'var(--bs-body-color)',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <FontAwesomeIcon icon={faComments} className="me-2" style={{ color: '#007bff' }} />
                  {language === 'es' ? 'Conversaciones' : 'Conversations'}
                  
                  {/* Indicador de estado */}
                  <span style={{
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    padding: '4px 12px',
                    borderRadius: '20px',
                    backgroundColor: autoRefreshEnabled ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)',
                    color: autoRefreshEnabled ? '#28a745' : '#dc3545',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: autoRefreshEnabled ? '#28a745' : '#dc3545',
                      animation: autoRefreshEnabled ? 'pulse 2s infinite' : 'none'
                    }}></span>
                    {autoRefreshEnabled 
                      ? (language === 'es' ? 'En línea' : 'Online')
                      : (language === 'es' ? 'Fuera de línea' : 'Offline')
                    }
                  </span>
                </h3>
                {lastUpdate && (
                  <small style={{ color: 'var(--bs-secondary)' }}>
                    {language === 'es' ? 'Actualizado:' : 'Updated:'} {new Date(lastUpdate).toLocaleTimeString('es-ES', { 
                      hour: '2-digit', 
                      minute: '2-digit',
                      second: '2-digit'
                    })}
                  </small>
                )}
              </div>
              
              {/* Toggle de auto-refresh */}
              <Button 
                variant={autoRefreshEnabled ? 'success' : 'secondary'}
                size="sm" 
                onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
                style={{ 
                  borderRadius: '8px',
                  padding: '8px 20px',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <FontAwesomeIcon 
                  icon={autoRefreshEnabled ? faCheckCircle : faTimesCircle} 
                />
                {autoRefreshEnabled 
                  ? (language === 'es' ? 'Pausar actualizaciones' : 'Pause updates')
                  : (language === 'es' ? 'Reanudar actualizaciones' : 'Resume updates')
                }
              </Button>
            </div>
          </Col>
        </Row>

        <Row className="mb-4 g-3">
          <Col xs={6} md={3}>
            <Card style={{ 
              border: 'none',
              borderRadius: '12px',
              backgroundColor: 'rgba(0,123,255,0.08)',
              padding: '20px'
            }}>
              <div className="d-flex align-items-center justify-content-between">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: '#007bff', 
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {language === 'es' ? 'Total' : 'Total'}
                  </div>
                  <div style={{ 
                    fontSize: '2rem', 
                    fontWeight: 700, 
                    color: '#007bff'
                  }}>
                    {conversations.length}
                  </div>
                </div>
                <FontAwesomeIcon 
                  icon={faComments} 
                  style={{ fontSize: '2rem', color: '#007bff', opacity: 0.3, flexShrink: 0 }} 
                />
              </div>
            </Card>
          </Col>

          <Col xs={6} md={3}>
            <Card style={{ 
              border: 'none',
              borderRadius: '12px',
              backgroundColor: 'rgba(40,167,69,0.08)',
              padding: '20px'
            }}>
              <div className="d-flex align-items-center justify-content-between">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: '#28a745', 
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {language === 'es' ? 'Activas' : 'Active'}
                  </div>
                  <div style={{ 
                    fontSize: '2rem', 
                    fontWeight: 700, 
                    color: '#28a745'
                  }}>
                    {activeConversations}
                  </div>
                </div>
                <FontAwesomeIcon 
                  icon={faCheckCircle} 
                  style={{ fontSize: '2rem', color: '#28a745', opacity: 0.3, flexShrink: 0 }} 
                />
              </div>
            </Card>
          </Col>

          <Col xs={6} md={3}>
            <Card style={{ 
              border: 'none',
              borderRadius: '12px',
              backgroundColor: 'rgba(255,193,7,0.08)',
              padding: '20px'
            }}>
              <div className="d-flex align-items-center justify-content-between">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: '#ffc107', 
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {language === 'es' ? 'Ventanas' : 'Windows'}
                  </div>
                  <div style={{ 
                    fontSize: '2rem', 
                    fontWeight: 700, 
                    color: '#ffc107'
                  }}>
                    {windows.length}
                  </div>
                </div>
                <FontAwesomeIcon 
                  icon={faWindowMaximize} 
                  style={{ fontSize: '2rem', color: '#ffc107', opacity: 0.3, flexShrink: 0 }} 
                />
              </div>
            </Card>
          </Col>

          <Col xs={6} md={3}>
            <Card style={{ 
              border: 'none',
              borderRadius: '12px',
              backgroundColor: 'rgba(23,162,184,0.08)',
              padding: '20px'
            }}>
              <div className="d-flex align-items-center justify-content-between">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: '#17a2b8', 
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {language === 'es' ? 'Mensajes' : 'Messages'}
                  </div>
                  <div style={{ 
                    fontSize: '2rem', 
                    fontWeight: 700, 
                    color: '#17a2b8'
                  }}>
                    {totalMessages.toLocaleString()}
                  </div>
                </div>
                <FontAwesomeIcon 
                  icon={faEnvelope} 
                  style={{ fontSize: '2rem', color: '#17a2b8', opacity: 0.3, flexShrink: 0 }} 
                />
              </div>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col>
            <Card style={{ 
              border: 'none',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}>
              {loading && conversations.length === 0 && (
                <div className="text-center py-5">
                  <FontAwesomeIcon icon={faSync} spin size="3x" style={{ color: '#007bff', marginBottom: '16px' }} />
                  <p style={{ color: 'var(--bs-secondary)' }}>
                    {language === 'es' ? 'Cargando conversaciones...' : 'Loading conversations...'}
                  </p>
                </div>
              )}

              {conversationsError && conversations.length === 0 && (
                <div className="p-4">
                  <div 
                    style={{
                      backgroundColor: 'rgba(220, 53, 69, 0.1)',
                      border: '2px solid rgba(220, 53, 69, 0.3)',
                      borderRadius: '16px',
                      padding: '32px',
                      textAlign: 'center'
                    }}
                  >
                    <FontAwesomeIcon 
                      icon={faExclamationTriangle} 
                      style={{ fontSize: '4rem', color: '#dc3545', marginBottom: '20px' }} 
                    />
                    <h4 style={{ color: '#dc3545', marginBottom: '12px', fontWeight: 600 }}>
                      {language === 'es' ? 'Error al cargar conversaciones' : 'Error loading conversations'}
                    </h4>
                    <p style={{ color: '#666', marginBottom: '24px', fontSize: '0.95rem' }}>
                      {conversationsError}
                    </p>
                    <Button
                      variant="danger"
                      onClick={refreshConversations}
                      style={{ 
                        borderRadius: '8px', 
                        padding: '10px 24px',
                        fontWeight: 500
                      }}
                    >
                      <FontAwesomeIcon icon={faSync} className="me-2" />
                      {language === 'es' ? 'Reintentar' : 'Retry'}
                    </Button>
                  </div>
                </div>
              )}

              {!loading && conversations.length === 0 && !conversationsError && (
                <div className="text-center py-5">
                  <div style={{ fontSize: '4rem', marginBottom: '20px', opacity: 0.3 }}>📭</div>
                  <h5 style={{ color: 'var(--bs-secondary)', marginBottom: '8px' }}>
                    {language === 'es' ? 'No hay conversaciones' : 'No conversations'}
                  </h5>
                  <p style={{ color: 'var(--bs-secondary)', fontSize: '0.9rem', opacity: 0.7 }}>
                    {language === 'es' 
                      ? 'Las conversaciones aparecerán aquí cuando recibas mensajes' 
                      : 'Conversations will appear here when you receive messages'}
                  </p>
                </div>
              )}

              {conversations.length > 0 && (
                <div style={{ overflowX: 'auto', overflowY: 'auto' }}>
                  <Table hover responsive style={{ marginBottom: 0, maxWidth: '99.6%' }}>
                    <thead style={{ backgroundColor: 'var(--bs-light)', borderBottom: '2px solid var(--bs-border-color)' }}>
                      <tr>
                        <th style={{ padding: '16px 20px', fontWeight: 600, fontSize: '0.85rem', minWidth: '200px', width: '20%' }}>
                          {language === 'es' ? 'Contacto' : 'Contact'}
                        </th>
                        <th style={{ padding: '16px 20px', fontWeight: 600, fontSize: '0.85rem', minWidth: '180px', width: '20%' }}>
                          {language === 'es' ? 'Último Mensaje' : 'Last Message'}
                        </th>
                        <th style={{ padding: '16px 20px', fontWeight: 600, fontSize: '0.85rem', textAlign: 'center', minWidth: '100px', width: '10%' }}>
                          {language === 'es' ? 'Estado' : 'Status'}
                        </th>
                        <th style={{ padding: '16px 20px', fontWeight: 600, fontSize: '0.85rem', textAlign: 'center', minWidth: '120px', width: '12%' }}>
                          {language === 'es' ? 'Etiqueta' : 'Tag'}
                        </th>
                        <th style={{ padding: '16px 20px', fontWeight: 600, fontSize: '0.85rem', textAlign: 'center', minWidth: '30px', width: '10%' }}>
                          {language === 'es' ? 'Rating' : 'Rating'}
                        </th>
                        <th style={{ padding: '16px 20px', fontWeight: 600, fontSize: '0.85rem', textAlign: 'center', minWidth: '30px', width: '6%' }}>
                          {language === 'es' ? 'Temp' : 'Temp'}
                        </th>
                        <th style={{ padding: '16px 20px', fontWeight: 600, fontSize: '0.85rem', textAlign: 'center', minWidth: '30px', width: '6%' }}>
                          {language === 'es' ? 'Modo' : 'Mode'}
                        </th>
                        <th style={{ padding: '16px 20px', fontWeight: 600, fontSize: '0.85rem', textAlign: 'center', minWidth: '30px', width: '6%' }}>
                          {language === 'es' ? 'Acciones' : 'Actions'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {conversations.map((conversation) => {
                        const displayName = getContactDisplayName(conversation.id);
                        const statusBadge = getStatusBadge(conversation);
                        const isPaused = pausedConversations[conversation.id] || false;
                        const isToggling = togglingPause[conversation.id] || false;
                        const isBanned = bannedConversations[conversation.id]?.isBanned || false;
                        
                        return (
                          <tr 
                            key={conversation.id}
                            onClick={() => handleConversationClick(conversation.id)}
                            style={{
                              cursor: 'pointer',
                              backgroundColor: selectedConversationId === conversation.id ? 'rgba(0,123,255,0.05)' : 'transparent',
                              position: 'relative',
                              zIndex: showColorPicker === conversation.id ? 1001 : (openDropdownId === conversation.id ? 1000 : 'auto')
                            }}
                            className="conversation-row"
                          >
                            <td style={{ padding: '16px 16px' }}>
                              <div className="d-flex align-items-center gap-3">
                                <div style={{
                                  width: '44px',
                                  height: '44px',
                                  borderRadius: '50%',
                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#fff',
                                  fontSize: '1rem',
                                  fontWeight: 600,
                                  flexShrink: 0
                                }}>
                                  {displayName.substring(0, 2).toUpperCase()}
                                </div>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                  <div style={{ 
                                    fontWeight: 600, 
                                    color: 'var(--bs-body-color)',
                                    fontSize: '0.90rem',
                                    marginBottom: '2px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                  title={displayName}
                                  >
                                    {displayName}
                                  </div>
                                  <div style={{ 
                                    fontSize: '0.75rem', 
                                    color: 'var(--bs-secondary)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {conversation.phoneNumber || conversation.id.split('@')[0]}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td style={{ padding: '16px 20px' }}>
                              <div style={{ minWidth: 0 }}>
                                <div 
                                  style={{ 
                                    fontSize: '0.85rem', 
                                    color: 'var(--bs-body-color)',
                                    marginBottom: '4px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                  title={conversation.preview || (language === 'es' ? 'Sin mensajes' : 'No messages')}
                                >
                                 {truncateText(
                                    conversation.preview || (language === 'es' ? 'Sin mensajes' : 'No messages'),
                                    30
                                    )
                                  }
                                </div>
                                <div style={{ 
                                  fontSize: '0.75rem', 
                                  color: 'var(--bs-secondary)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}>
                                  {formatRelativeTime(conversation.lastMessageTime)}
                                </div>
                              </div>
                            </td>

                            <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                              <Badge 
                                bg={statusBadge.bg}
                                style={{ 
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  padding: '4px 10px',
                                  borderRadius: '4px'
                                }}
                              >
                                {statusBadge.text}
                              </Badge>
                            </td>

                            <td style={{ padding: '16px 20px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                              <div style={{ position: 'relative', display: 'inline-block', width: '100%', maxWidth: '130px', margin: '0 auto' }}>
                                {/* ✅ SIEMPRE mostrar botón de color */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowColorPicker(showColorPicker === conversation.id ? null : conversation.id);
                                  }}
                                  style={{
                                    position: 'absolute',
                                    left: '4px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: '20px',
                                    height: '20px',
                                    border: conversationTagColors[conversation.id] 
                                    ? '2px solid rgba(255,255,255,0.8)'  // Borde blanco si hay color
                                    : '2px dashed var(--bs-border-color)',  // Borde punteado si NO hay color
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    padding: '0',
                                    backgroundColor: conversationTagColors[conversation.id] || '#e9ecef',  // ✅ Gris por defecto
                                    zIndex: 10,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!conversationTagColors[conversation.id]) {
                                      e.currentTarget.style.backgroundColor = '#dee2e6';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!conversationTagColors[conversation.id]) {
                                      e.currentTarget.style.backgroundColor = '#e9ecef';
                                    }
                                  }}
                                  title={conversationTagColors[conversation.id] ? "Cambiar color" : "Agregar color"}
                                >
                                  {/* ✅ Ícono + si no hay color */}
                                  {!conversationTagColors[conversation.id] && (
                                  <span style={{ 
                                      color: '#6c757d', 
                                      fontSize: '12px', 
                                      fontWeight: 'bold',
                                      lineHeight: 1
                                    }}>+</span>
                                  )}
                                </button>
                                <Form.Control
                                  type="text"
                                  size="sm"
                                  placeholder="Sin etiqueta"
                                  value={conversationTags[conversation.id] || ''}
                                  onChange={(e) => handleTagChange(conversation.id, e.target.value)}
                                  onFocus={() => setEditingTagId(conversation.id)}
                                  onBlur={(e) => handleTagSave(conversation.id, e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.currentTarget.blur();
                                    }
                                  }}
                                  style={{
                                    fontSize: '0.75rem',
                                    textAlign: 'center',
                                        width: '100%',
                                        paddingLeft: '28px',  // ✅ SIEMPRE dejar espacio para el botón
                                        paddingRight: '8px',
                                        backgroundColor: conversationTagColors[conversation.id] || 'var(--bs-body-bg)',
                                        color: conversationTagColors[conversation.id] 
                                          ? (TAG_COLORS.find(c => c.value === conversationTagColors[conversation.id])?.text || '#fff')
                                        : 'var(--bs-body-color)',
                                        border: conversationTagColors[conversation.id] 
                                        ? `1px solid ${conversationTagColors[conversation.id]}`
                                        : '1px solid var(--bs-border-color)',
                                        fontWeight: conversationTagColors[conversation.id] ? 500 : 400,
                                        borderRadius: '6px'
                                      }}
                                    />
                                    {showColorPicker === conversation.id && (
                                      <div
                                        style={{
                                          position: 'absolute',
                                          top: '100%',
                                          left: '50%',
                                          transform: 'translateX(-50%)',
                                          marginTop: '8px',
                                          backgroundColor: 'var(--bs-body-bg)',
                                          border: '1px solid var(--bs-border-color)',
                                          borderRadius: '8px',
                                          padding: '8px',
                                          boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                                          zIndex: 9999,
                                          display: 'grid',
                                          gridTemplateColumns: 'repeat(4, 1fr)',
                                          gap: '6px',
                                          minWidth: '140px'
                                        }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                    {/* Opción sin color */}
                                    <button
                                      onClick={() => {
                                        setConversationTagColors(prev => {
                                          const newColors = { ...prev };
                                          delete newColors[conversation.id];
                                          return newColors;
                                        });
                                        handleTagSave(conversation.id, conversationTags[conversation.id] || '', '');
                                        setShowColorPicker(null);
                                      }}
                                      style={{
                                        width: '28px',
                                          height: '28px',
                                          border: '2px solid var(--bs-border-color)',
                                          borderRadius: '50%',
                                          cursor: 'pointer',
                                          padding: '0',
                                          backgroundColor: '#fff',
                                          position: 'relative',
                                          gridColumn: 'span 4'
                                        }}
                                        title="Sin color"
                                      >
                                        <div style={{
                                          position: 'absolute',
                                          top: '50%',
                                          left: '50%',
                                          transform: 'translate(-50%, -50%) rotate(45deg)',
                                          width: '20px',
                                          height: '2px',
                                          backgroundColor: '#dc3545'
                                        }} />
                                      </button>
                                    {TAG_COLORS.map((color) => (
                                      <button
                                        key={color.value}
                                          onClick={() => {
                                          setConversationTagColors(prev => ({ ...prev, [conversation.id]: color.value }));
                                          handleTagSave(conversation.id, conversationTags[conversation.id] || '', color.value);
                                          setShowColorPicker(null);
                                        }}
                                        style={{
                                          width: '28px',  
                                          height: '28px',
                                          border: conversationTagColors[conversation.id] === color.value 
                                          ? '3px solid var(--bs-primary)'
                                          : '2px solid rgba(0,0,0,0.1)',
                                          borderRadius: '50%',
                                          cursor: 'pointer',
                                          padding: '0',
                                          backgroundColor: color.value,
                                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                          transition: 'transform 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                        title={color.name}
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '16px 20px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                              <StarRating 
                                rating={conversationRatings[conversation.id] || 0}
                                onChange={(rating) => handleRatingChange(conversation.id, rating)}
                              />
                            </td>

                            <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                              <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <TemperatureMeter temperature={conversationTemperatures[conversation.id] || 0} />
                              </div>
                            </td>

                            <td style={{ padding: '16px 20px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                              <div className="d-flex justify-content-center gap-2">
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => !isToggling && handleTogglePause(conversation.id)}
                                  style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '0px 0px',
                                  borderRadius: '6px',
                                  cursor: isToggling ? 'wait' : 'pointer',
                                  transition: 'all 0.3s ease',
                                  border: '1px solid rgba(255,255,255,0.2)',
                                  boxShadow: '0 0px 1px rgba(0,0,0,0.15)',
                                  opacity: isToggling ? 0.7 : 1
                                }}
                                  title={isPaused ? "Modo Manual - Click para activar IA" : "Modo IA - Click para pausar"}
                                >
                                  <FontAwesomeIcon 
                                  icon={isPaused ? faUser : faRobot} 
                                  style={{ 
                                    borderRadius: '6px', 
                                    padding: '5.5px 12px',
                                    border: '1px solid #43d40aff',
                                  }} 
                                />
                                </Button>
                              </div>
                            </td>

                            <td style={{ padding: '16px 20px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                              <Dropdown 
                                align="end" 
                                onToggle={(isOpen) => setOpenDropdownId(isOpen ? conversation.id : null)}
                              >
                                <Dropdown.Toggle
                                  variant="outline-secondary"
                                  size="sm"
                                  style={{
                                    borderRadius: '6px',
                                    padding: '4px 8px',
                                    border: '1px solid var(--bs-border-color)'
                                  }}
                                >
                                  <FontAwesomeIcon icon={faEllipsisV} />
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                  {isBanned ? (
                                    <Dropdown.Item onClick={() => handleUnban(conversation.id)}>
                                      <FontAwesomeIcon icon={faUnlock} className="me-2" style={{ color: '#28a745' }} />
                                      {language === 'es' ? 'Desbloquear' : 'Unban'}
                                    </Dropdown.Item>
                                  ) : (
                                    <>
                                      <Dropdown.Item onClick={() => handleBanPermanent(conversation.id, displayName)}>
                                        <FontAwesomeIcon icon={faBan} className="me-2" style={{ color: '#dc3545' }} />
                                        {language === 'es' ? 'Bloqueo permanente' : 'Permanent ban'}
                                      </Dropdown.Item>
                                      <Dropdown.Item onClick={() => handleBanTemporary(conversation.id, displayName)}>
                                        <FontAwesomeIcon icon={faClock} className="me-2" style={{ color: '#ffc107' }} />
                                        {language === 'es' ? 'Bloqueo temporal' : 'Temporary ban'}
                                      </Dropdown.Item>
                                      <Dropdown.Divider />
                                    </>
                                  )}
                                  <Dropdown.Item 
                                    onClick={() => handleDeleteConversation(conversation.id, displayName)}
                                    style={{ color: '#dc3545' }}
                                  >
                                    <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                                    {language === 'es' ? 'Eliminar' : 'Delete'}
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </Container>

      {windows.filter(w => !w.isMinimized).map(window => {
        const conversation = getConversationById(window.conversationId);
        const displayName = getContactDisplayName(window.conversationId);

        return (
          <ConversationWindow
            key={window.id}
            id={window.id}
            conversationId={window.conversationId}
            displayName={displayName}
            position={window.position}
            size={window.size}
            zIndex={window.zIndex}
            onClose={() => closeWindow(window.id)}
            onMinimize={() => minimizeWindow(window.id)}
            onPositionChange={(pos) => updateWindowPosition(window.id, pos)}
            onSizeChange={(size) => updateWindowSize(window.id, size)}
            onBringToFront={() => bringToFront(window.id)}
            isPaused={pausedConversations[window.conversationId] || false}
            copilotEnabled={copilotEnabled}
            onTogglePause={() => handleTogglePause(window.conversationId)}
            isTogglingPause={togglingPause[window.conversationId] || false}
            isBanned={bannedConversations[window.conversationId]?.isBanned || false}
            onToggleBan={() => {
              const isBanned = bannedConversations[window.conversationId]?.isBanned;
              if (isBanned) {
                handleUnban(window.conversationId);
              } else {
                handleBanPermanent(window.conversationId, displayName);
              }
            }}
            isTogglingBan={togglingBan[window.conversationId] || false}
          />
        );
      })}

      {windows.filter(w => w.isMinimized).map(window => {
        const conversation = getConversationById(window.conversationId);
        const displayName = getContactDisplayName(window.conversationId);

        return (
          <ConversationBubble
            key={window.id}
            id={window.id}
            conversationId={window.conversationId}
            displayName={displayName}
            unreadCount={0}
            isActive={conversation?.isActive}
            position={window.position}
            zIndex={window.zIndex}
            onMaximize={() => maximizeWindow(window.id)}
            onClose={() => closeWindow(window.id)}
            onDrag={(pos) => updateWindowPosition(window.id, pos)}
            onBringToFront={() => bringToFront(window.id)}
            isPaused={pausedConversations[window.conversationId] || false}
            copilotEnabled={copilotEnabled}
            onTogglePause={() => handleTogglePause(window.conversationId)}
            isTogglingPause={togglingPause[window.conversationId] || false}
          />
        );
      })}

      <style>{`
        .conversation-row {
          transition: all 0.2s ease;
          border-bottom: 1px solid var(--bs-border-color);
        }

        .conversation-row:hover {
          background-color: rgba(0,123,255,0.05) !important;
          transform: translateX(2px);
        }

        .conversation-row:last-child {
          border-bottom: none;
        }

        .dropdown-menu {
          z-index: 9999 !important;
          box-shadow: 0 4px 16px rgba(0,0,0,0.15) !important;
        }

        .dropdown.show {
          z-index: 9998 !important;
        }

        .table tbody tr {
          position: relative;
        }
        
        /* Ensure color picker is always on top */
        .table tbody tr[style*="z-index: 1001"] {
          z-index: 1001 !important;
        }

        .modal.show .modal-dialog {
          animation: modalSlideIn 0.3s ease-out;
        }

        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .toast {
          animation: toastSlideIn 0.3s ease-out;
        }

        @keyframes toastSlideIn {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        @media (max-width: 768px) {
          .conversation-row td {
            padding: 12px 16px !important;
          }

          .conversation-row td:nth-child(1) div {
            gap: 8px !important;
          }

          .conversation-row td:nth-child(1) > div > div:first-child {
            width: 36px !important;
            height: 36px !important;
            font-size: 0.9rem !important;
          }
        }

        body {
          overflow-x: hidden;
        }

        .table-responsive {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        @media (max-width: 576px) {
          .modal-dialog {
            margin: 0.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default ConversationsTable;